/*
* (C) Behaviour Interactive Inc. - All Rights Reserved
* Unauthorized copying of this file, via any medium, is strictly prohibited
* This file is proprietary and confidential
*/

import React, { useEffect, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { RegistrationStatus } from '../../Account.model';
import { GameConfig } from '../../../Config.model';
import { PreRegistrations } from '../../../authentication/Authentication.model';
import { ProviderName } from '../../providers/ProviderEnum';
import { getProviderLogo } from '../../providers/ProviderLogo';

interface Props {
  gameConfig: GameConfig;
  gameImage: string;
  // TODO: Convert object to Map for easier access
  userPreRegistration: PreRegistrations[keyof PreRegistrations];
  isNew?: boolean;
  ownedOnProviders?: ProviderName[];
}

export function GameCard({
  gameConfig,
  gameImage,
  userPreRegistration,
  isNew,
  ownedOnProviders = [],
}: Props): JSX.Element {
  const [preRegState, setPreRegState] = useState<RegistrationStatus>();

  const { t } = useTranslation();
  const history = useHistory();

  const getUserPreRegistrationStatus = (): void => {
    const preRegId = gameConfig.preRegistration.id as keyof PreRegistrations[keyof PreRegistrations];
    if (!Object.keys(gameConfig.preRegistration).length) {
      setPreRegState(RegistrationStatus.UNAVAILABLE);
    } else if (!userPreRegistration?.[preRegId]?.status) {
      setPreRegState(RegistrationStatus.UNREGISTERED);
    } else {
      setPreRegState(userPreRegistration[preRegId].status as RegistrationStatus);
    }
  };
  useEffect(() => {
    getUserPreRegistrationStatus();
  }, [gameConfig, userPreRegistration]);

  return (
    <div
      key={gameConfig.id}
      className='game-card'
      data-cy={`games-${gameConfig.id}-button`}
    >
      <Link to={`/account/my-account/games/${gameConfig.id}`}>
        <div className='game-card-img' style={{ backgroundImage: `url('${gameImage}')` }}>
          {isNew && (
            <>
              <div className={`game-new ${!gameConfig.preRegistration?.id ? 'pre-reg-disabled' : ''}`}>
                <span className='badge new-badge'>
                  {t('account.my-account.games.new')}
                </span>
              </div>
            </>
          )}

          {preRegState !== RegistrationStatus.UNAVAILABLE && (
            <>
              <div className='game-registration-status' data-cy={`${gameConfig.id}-game-registration-status`}>
                { preRegState === RegistrationStatus.UNREGISTERED
                  && <InfoIcon className='icon info' /> }

                { preRegState === RegistrationStatus.REGISTERED
                  && <CheckCircleIcon className='icon success' /> }

                { preRegState === RegistrationStatus.SELECTED_NO_CLAIM
                  && <CheckCircleIcon className='icon success' /> }

                { preRegState === RegistrationStatus.SELECTED
                  && <StarIcon className='icon gold' /> }

                { preRegState === RegistrationStatus.GRANTED
                  && <AutoAwesomeIcon className='icon gold' /> }
                <span
                  className='label'
                  data-cy={`thumbnail-${gameConfig.id}-${preRegState}`}
                >
                  {t(`account.my-account.earlyAccess.${preRegState}`)}
                </span>
              </div>
            </>
          )}
        </div>
        <div className='game-card-title'>
          <div>{t(`games.${gameConfig.id}`)}</div>
          <div className='providers-logo'>
            {ownedOnProviders.map((provider) => (
              <img
                src={getProviderLogo(provider)}
                alt={provider}
                key={`${gameConfig.id}-${provider}-logo`}
              />
            ))}
          </div>
        </div>
      </Link>
      <div className='game-card-overlay'>
        <button
          type='button'
          className='btn btn-secondary btn-block mx-auto mt-3 mb-0'
          data-cy={`${gameConfig.id}-discover-btn`}
          onClick={() => history.push(`/account/my-account/games/${gameConfig.id}`)}
        >
          {t('button.discover-more')}
        </button>
      </div>
    </div>
  );
}
